import type { ContractRevisionView, ContractVersionView } from "@closer/shared";
import { attributionLabel, CONTRACT_REVISION_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, Card, EmptyState, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ContractAiRevisionPanel } from "./ContractAiRevisionPanel";
import type { OneriGirdisi } from "./RevisionProposalDialog";
import { RevisionProposalDialog } from "./RevisionProposalDialog";

/**
 * REVİZYON ÖNERİLERİ (CLOS-138 `[093]`, CLOS-139 `[094]`).
 *
 * ⚠ Bu ekran yoktu. Sunucudaki liste, karar ve uygulama uçları aylardır hazırdı ama
 * ürünün hiçbir yerinden onlara gidilemiyordu; iki kart da "Tamamlandı" görünüyordu.
 *
 * ### ⚠ Üç eylem, üç ayrı yetki
 *
 * Görüntüleme `contract.view`, karar `contract.review`, metne uygulama `contract.edit`.
 * Kart bunların ayrı doğrulanmasını istiyor: tek bir yetkiyle üçünü birden açsaydık,
 * öneriyi okuyabilen herkes onu kabul edebilir ve metni değiştirebilirdi.
 *
 * ### ⚠ Kabul etmek uygulamak DEĞİL
 *
 * Kabul "bu değişikliği onaylıyorum", uygulama "sözleşme metnini şimdi değiştiriyorum"
 * demek. Kabul edilen öneri otomatik uygulansaydı, onay veren kişi farkında olmadan metni
 * de değiştirmiş olurdu — geri alması kolay olmayan bir sonuç.
 *
 * ### ⚠ Bayat hedef sessiz kalmıyor
 *
 * Öneri yapıldıktan sonra hedef metin değiştiyse (`staleTarget`) karar VERİLMEDEN önce
 * uyarı çıkıyor: kullanıcı artık var olmayan bir metne dair bir öneriyi onaylayabilirdi.
 */
export function ContractRevisionsPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [oneriAcik, setOneriAcik] = useState(false);
  const anahtar = tenantKey(tenantId, "contract", id, "revizyonlar");

  /**
   * Öneri oluşturma penceresi kaynak versiyonu seçtiriyor; liste yalnız pencere açıkken
   * çekiliyor — kapalıyken ekranın ona ihtiyacı yok.
   */
  const versiyonlar = useQuery({
    enabled: oneriAcik,
    queryKey: tenantKey(tenantId, "contract", id, "versiyonlar", "oneri-secimi"),
    queryFn: async () =>
      (await api<ContractVersionView[]>(`/api/contracts/${id}/versions?limit=50`, { tenantId }))
        .data,
  });

  const sorgu = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractRevisionView[]>(`/api/contracts/${id}/revisions`, { tenantId })).data,
  });

  const tazele = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
  };

  const karar = useMutation({
    mutationFn: async (girdi: { revisionId: string; decision: "accepted" | "rejected" }) =>
      (
        await api(`/api/contracts/${id}/revisions/${girdi.revisionId}/decision`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ decision: girdi.decision }),
        })
      ).data,
    onSuccess: async (_veri, girdi) => {
      await tazele();
      toast.success(girdi.decision === "accepted" ? "Öneri kabul edildi" : "Öneri reddedildi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Karar kaydedilemedi");
    },
  });

  const oneriOlustur = useMutation({
    mutationFn: async (girdi: OneriGirdisi) =>
      (
        await api(`/api/contracts/${id}/revisions`, {
          method: "POST",
          tenantId,
          body: JSON.stringify(girdi),
        })
      ).data,
    onSuccess: async () => {
      setOneriAcik(false);
      await tazele();
      toast.success("Revizyon önerisi oluşturuldu");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Öneri oluşturulamadı");
    },
  });

  const uygula = useMutation({
    mutationFn: async (revisionId: string) =>
      (
        await api(`/api/contracts/${id}/revisions/${revisionId}/apply`, {
          method: "POST",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      await tazele();
      toast.success("Öneri metne uygulandı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Öneri uygulanamadı");
    },
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Revizyon önerileri yükleniyor">
        <Skeleton lines={4} />
      </div>
    );
  }

  /**
   * ⚠ Hata dalı ŞART: kaldırılırsa liste boş çizilir ve erişimini kaybetmiş kullanıcıya
   * "henüz öneri yok" denir — ekran yanlış bir gerçek uydurur.
   */
  if (sorgu.error || !sorgu.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Öneriler görüntülenemiyor",
                description: "Bu sözleşme yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}`}
        >
          Sözleşmeye dön
        </Link>
      </div>
    );
  }

  const oneriler = sorgu.data;

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">
          Revizyon önerileri
        </h1>
        {can("contract.revision") ? (
          <Button type="button" size="sm" onClick={() => setOneriAcik(true)}>
            Öneri oluştur
          </Button>
        ) : null}
      </div>

      {/*
        AI REVİZYON ÖNERİSİ (CLOS-207 `[190]`) — manuel öneri formunun YANINDA, onun
        yerine değil. AI çıktısı da bu sayfadaki kanonik listeye ancak açık bir "kaydet"
        komutuyla giriyor.
      */}
      <ContractAiRevisionPanel contractId={id} />

      <RevisionProposalDialog
        open={oneriAcik}
        onClose={() => setOneriAcik(false)}
        contractId={id}
        versiyonlar={versiyonlar.data ?? []}
        onConfirm={(girdi) => oneriOlustur.mutate(girdi)}
        isPending={oneriOlustur.isPending}
      />

      {oneriler.length === 0 ? (
        <EmptyState title="Henüz revizyon önerisi yok." />
      ) : (
        <div className="flex flex-col gap-3">
          {oneriler.map((oneri) => (
            <Card
              key={oneri.id}
              title={`Versiyon ${oneri.sourceVersionNumber} · madde önerisi`}
              actions={
                <Badge tone={oneri.status === "open" ? "warning" : "neutral"}>
                  {CONTRACT_REVISION_STATUS_NAMES[oneri.status] ?? oneri.status}
                </Badge>
              }
            >
              <div className="flex flex-col gap-3">
                {/* Bayat hedef bir HATA değil, karar verirken bilinmesi gereken bir durum. */}
                {oneri.staleTarget ? (
                  <p role="alert" className="text-sm text-warning">
                    Bu önerinin hedef metni öneriden sonra değişti; karar vermeden önce güncel metni
                    kontrol edin.
                  </p>
                ) : null}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-fg-muted">Mevcut metin</p>
                    <p className="text-sm text-fg">{oneri.currentText}</p>
                  </div>
                  <div>
                    <p className="text-xs text-fg-muted">Önerilen metin</p>
                    <p className="text-sm text-fg-strong">{oneri.proposedText}</p>
                  </div>
                </div>

                {oneri.note ? <p className="text-sm text-fg-soft">{oneri.note}</p> : null}

                <p className="text-xs text-fg-soft">
                  {/*
                    Öneri kişiye değil TARAFA bağlı: "SAN Otomotiv A.Ş. adına Ayşe Demir" — cümle
                    tek yardımcıdan (CLOS-1152), aktivite akışıyla aynı. Versiyon kart başlığında.
                  */}
                  {attributionLabel({
                    actorName: oneri.createdByName,
                    onBehalfOf: oneri.representedPartyName,
                  })}
                  {oneri.resolvedByName ? ` · kararı: ${oneri.resolvedByName}` : null}
                  {oneri.appliedVersionNumber !== null
                    ? ` · uygulandı: v${oneri.appliedVersionNumber}`
                    : null}
                </p>

                <div className="flex flex-wrap justify-end gap-2">
                  {oneri.status === "open" && can("contract.review") ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={karar.isPending}
                        onClick={() => karar.mutate({ revisionId: oneri.id, decision: "rejected" })}
                      >
                        Reddet
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        loading={karar.isPending}
                        onClick={() => karar.mutate({ revisionId: oneri.id, decision: "accepted" })}
                      >
                        Kabul et
                      </Button>
                    </>
                  ) : null}

                  {/* ⚠ Yalnız KABUL EDİLMİŞ ve henüz uygulanmamış öneri metne işlenebilir. */}
                  {oneri.status === "accepted" &&
                  oneri.appliedVersionId === null &&
                  can("contract.edit") ? (
                    <Button
                      type="button"
                      size="sm"
                      loading={uygula.isPending}
                      onClick={() => uygula.mutate(oneri.id)}
                    >
                      Metne uygula
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Link
        className="text-sm font-medium text-primary hover:underline"
        to={`/app/sozlesmeler/${id}`}
      >
        Sözleşmeye dön
      </Link>
    </div>
  );
}
